import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { COMPANY_INFO } from "../../../data/company";
import SectionHeader from "../../common/SectionHeader/SectionHeader";
import AnimatedSection from "../../common/AnimatedSection/AnimatedSection";
import Card, { CardBody, CardHeader } from "../../ui/Card/Card";
import Button from "../../ui/Button/Button";
import "./ContactSection.css";
import { useLanguage } from "../../../context/LanguageContext";

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  message: string;
}

export const ContactSection: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>();

  // Language hook must be inside the component body
  const { t } = useLanguage();

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setSubmitMessage(t('contact.success'));
      setIsSubmitting(false);
      reset();
    }, 2000);
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <AnimatedSection animation="fadeInUp" delay={0.2}>
          <SectionHeader
            subtitle={t('contact.subtitle')}
            title={t('contact.title')}
            description={t('contact.description')}
          />
        </AnimatedSection>

        <div className="contact__content">
          {/* Contact Form */}
          <AnimatedSection animation="fadeInLeft" delay={0.4}>
            <div className="contact__form-section">
            <Card variant="elevated" className="contact__form-card">
              <CardHeader>
                <h3 className="contact__form-title">{t('contact.form.title')}</h3>
                <p className="contact__form-subtitle">
                  {t('contact.form.subtitle')}
                </p>
              </CardHeader>

              <CardBody>
                {submitMessage && (
                  <div className="contact__success-message">
                    <i className="fas fa-check-circle"></i>
                    <p>{submitMessage}</p>
                  </div>
                )}

                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="contact__form"
                >
                  <div className="contact__form-row">
                    <div className="contact__form-group">
                      <label htmlFor="name">{t('contact.labels.name')}</label>
                      <input
                        type="text"
                        id="name"
                        {...register("name", {
                          required: t('contact.errors.nameRequired'),
                        })}
                        className={errors.name ? "error" : ""}
                      />
                      {errors.name && (
                        <span className="contact__error">
                          {errors.name.message}
                        </span>
                      )}
                    </div>

                    <div className="contact__form-group">
                      <label htmlFor="email">{t('contact.labels.email')}</label>
                      <input
                        type="email"
                        id="email"
                        {...register("email", {
                          required: t('contact.errors.emailRequired'),
                          pattern: {
                            value: /^\S+@\S+$/i,
                            message: t('contact.errors.emailInvalid'),
                          },
                        })}
                        className={errors.email ? "error" : ""}
                      />
                      {errors.email && (
                        <span className="contact__error">
                          {errors.email.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="contact__form-row">
                    <div className="contact__form-group">
                      <label htmlFor="phone">{t('contact.labels.phone')}</label>
                      <input type="tel" id="phone" {...register("phone")} />
                    </div>

                    <div className="contact__form-group">
                      <label htmlFor="company">{t('contact.labels.company')}</label>
                      <input
                        type="text"
                        id="company"
                        {...register("company")}
                      />
                    </div>
                  </div>

                  <div className="contact__form-group">
                    <label htmlFor="subject">{t('contact.labels.subject')}</label>
                      <input
                        type="text"
                        id="subject"
                      {...register("subject", { required: t('contact.errors.subjectRequired') })}
                      className={errors.subject ? "error" : ""}
                    />
                    {errors.subject && (
                      <span className="contact__error">
                        {errors.subject.message}
                      </span>
                    )}
                  </div>

                  <div className="contact__form-group">
                    <label htmlFor="message">{t('contact.labels.message')}</label>
                      <textarea
                        id="message"
                        rows={5}
                        {...register("message", { required: t('contact.errors.messageRequired') })}
                        className={errors.message ? "error" : ""}
                      ></textarea>
                    {errors.message && (
                      <span className="contact__error">
                        {errors.message.message}
                      </span>
                    )}
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    loading={isSubmitting}
                    fullWidth
                    leftIcon={<i className="fas fa-paper-plane"></i>}
                  >
                    {t('contact.submit')}
                  </Button>
                </form>
              </CardBody>
            </Card>
            </div>
          </AnimatedSection>

          {/* Contact Info */}
          <AnimatedSection animation="fadeInRight" delay={0.5}>
            <div className="contact__info-section">
            {/* Contact Details */}
            <Card variant="elevated" className="contact__info-card">
              <CardBody>
                <h3 className="contact__info-title">{t('contact.info.title')}</h3>

                <div className="contact__info-item">
                  <div className="contact__info-icon">
                    <i className="fas fa-map-marker-alt"></i>
                  </div>
                  <div className="contact__info-content">
                    <h4>{t('contact.info.address')}</h4>
                    <p>{COMPANY_INFO.contact.address.fullAddress}</p>
                  </div>
                </div>

                <div className="contact__info-item">
                  <div className="contact__info-icon">
                    <i className="fas fa-phone"></i>
                  </div>
                  <div className="contact__info-content">
                    <h4>{t('contact.info.phone')}</h4>
                    {COMPANY_INFO.contact.phone.map((phone, index) => (
                      <p key={index}>
                        <a href={`tel:${phone}`}>{phone}</a>
                      </p>
                    ))}
                  </div>
                </div>

                <div className="contact__info-item">
                  <div className="contact__info-icon contact__info-icon--whatsapp">
                    <i className="fab fa-whatsapp"></i>
                  </div>
                  <div className="contact__info-content">
                    <h4>{t('contact.info.whatsapp')}</h4>
                    <p>
                      <a 
                        href={`https://wa.me/${COMPANY_INFO.contact.whatsapp.replace(/\s/g, '').replace('+', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact__whatsapp-link"
                      >
                        {COMPANY_INFO.contact.whatsapp}
                      </a>
                    </p>
                  </div>
                </div>

                <div className="contact__info-item">
                  <div className="contact__info-icon">
                    <i className="fas fa-envelope"></i>
                  </div>
                  <div className="contact__info-content">
                    <h4>{t('contact.info.email')}</h4>
                    {COMPANY_INFO.contact.email.map((email, index) => (
                      <p key={index}>
                        <a href={`mailto:${email}`}>{email}</a>
                      </p>
                    ))}
                  </div>
                </div>

                <div className="contact__info-item">
                  <div className="contact__info-icon">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="contact__info-content">
                    <h4>{t('contact.info.hours')}</h4>
                    <p>{COMPANY_INFO.contact.workingHours.weekdays}</p>
                    <p>{COMPANY_INFO.contact.workingHours.saturday}</p>
                    <p>{COMPANY_INFO.contact.workingHours.sunday}</p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Map Placeholder */}
            <Card variant="elevated" className="contact__map-card">
              <CardBody padding="none">
                <div className="contact__map-placeholder">
                  <i className="fas fa-map"></i>
                  <h4>{t('contact.map')}</h4>
                  <p>{COMPANY_INFO.contact.address.fullAddress}</p>
                  <p>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(COMPANY_INFO.contact.address.fullAddress)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact__whatsapp-link"
                    >
                      {t('contact.map.open')}
                    </a>
                  </p>
                </div>
              </CardBody>
            </Card>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
